import { useState } from "react";
import { useQuery } from "react-query";
import { useNavigate } from "react-router-dom";
import { Loader, Send, Phone, Eye, Clock, AlertTriangle, CheckCircle, Bell, Filter, Users, PlayCircle, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { api } from "@/lib/api";
import { formatCurrency, cn } from "@/lib/utils";
import { format } from "date-fns";
import { toast } from "sonner";
import { useOverdueSummary, useReminderHistory, useSendReminder, useBulkSendReminders, useRunReminderScan } from "@/hooks/useLoans";

const REMINDER_TYPE_LABELS: Record<string, string> = {
  before_due_7d: "7 days before",
  before_due_3d: "3 days before",
  before_due_1d: "1 day before",
  due_today: "Due today",
  overdue_1d: "1 day overdue",
  overdue_3d: "3 days overdue",
  overdue_7d: "7 days overdue",
};

const REMINDER_STATUS_COLORS: Record<string, string> = {
  pending: "bg-amber-100 text-amber-700",
  sent: "bg-green-100 text-green-700",
  failed: "bg-red-100 text-red-700",
  cancelled: "bg-gray-100 text-gray-600",
};

const PAYMENT_STATUS_COLORS: Record<string, string> = {
  pending: "bg-amber-100 text-amber-700",
  partial: "bg-orange-100 text-orange-700",
  overdue: "bg-red-100 text-red-700",
  paid: "bg-green-100 text-green-700",
};

export default function RepaymentReminders() {
  const navigate = useNavigate();
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [historyPage, setHistoryPage] = useState(1);

  const { data: overdueData, isLoading: overdueLoading, refetch: refetchOverdue } = useOverdueSummary();
  const { data: historyData, isLoading: historyLoading } = useReminderHistory({
    status: statusFilter === "all" ? undefined : statusFilter,
    page: historyPage,
    limit: 20,
  });
  const sendReminder = useSendReminder();
  const bulkSend = useBulkSendReminders();
  const runScan = useRunReminderScan();

  const overdueItems = overdueData || [];
  const historyItems = historyData?.items || [];
  const historyTotal = historyData?.total || 0;

  const filteredOverdue = overdueItems.filter((item: any) => {
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      return (
        item.memberName?.toLowerCase().includes(q) ||
        item.loanCode?.toLowerCase().includes(q)
      );
    }
    return true;
  });

  const handleSendReminder = async (loanId: number, reminderId?: number) => {
    if (!reminderId) {
      toast.error("No pending reminder to send");
      return;
    }
    try {
      await sendReminder.mutateAsync({ loanId, reminderId });
      toast.success("Reminder sent successfully");
      refetchOverdue();
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Failed to send reminder");
    }
  };

  const handleBulkSend = async () => {
    if (selectedIds.length === 0) {
      toast.error("Select reminders to send");
      return;
    }
    try {
      const result = await bulkSend.mutateAsync(selectedIds);
      toast.success(`Sent ${result.sent} reminders, ${result.failed} failed`);
      setSelectedIds([]);
      refetchOverdue();
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Bulk send failed");
    }
  };

  const handleRunScan = async () => {
    try {
      const result = await runScan.mutateAsync();
      toast.success(`Scan complete: ${result.created} created, ${result.skipped} skipped`);
      refetchOverdue();
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Scan failed");
    }
  };

  const toggleSelect = (id: number) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
    );
  };

  const toggleSelectAll = () => {
    const allIds = filteredOverdue.map((item: any) => item.scheduleId);
    if (selectedIds.length === allIds.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(allIds);
    }
  };

  return (
    <div className="sm:px-4 px-3 pt-3 pb-8">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-xl sm:text-2xl font-bold">Repayment Reminders</h1>
        <p className="text-sm text-gray-500 mt-1">Manage payment reminders for overdue and upcoming loan installments</p>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
        {[
          { label: "Overdue Members", value: overdueItems.length, icon: AlertTriangle, color: "text-red-600", bg: "bg-red-50" },
          { label: "Total Overdue Amount", value: formatCurrency(overdueItems.reduce((s: number, i: any) => s + (i.amountDue || 0), 0)), icon: Clock, color: "text-amber-600", bg: "bg-amber-50" },
          { label: "Reminders Sent", value: historyItems.filter((i: any) => i.status === "sent").length, icon: CheckCircle, color: "text-green-600", bg: "bg-green-50" },
          { label: "Pending Reminders", value: historyItems.filter((i: any) => i.status === "pending").length, icon: Bell, color: "text-blue-600", bg: "bg-blue-50" },
        ].map((card) => (
          <div key={card.label} className={`${card.bg} rounded-lg border border-gray-100 p-3 sm:p-4`}>
            <div className="flex items-center gap-2 mb-1.5">
              <card.icon className={`h-4 w-4 ${card.color}`} />
              <span className="text-xs font-medium text-gray-500 truncate">{card.label}</span>
            </div>
            <p className={`text-lg font-bold ${card.color}`}>{card.value}</p>
          </div>
        ))}
      </div>

      {/* Actions Bar */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 mb-4">
        <div className="flex items-center gap-2 flex-wrap">
          <Button
            size="sm"
            variant="outline"
            onClick={handleRunScan}
            disabled={runScan.isLoading}
            className="h-8 text-xs"
          >
            {runScan.isLoading ? <Loader className="h-3.5 w-3.5 animate-spin mr-1.5" /> : <PlayCircle className="h-3.5 w-3.5 mr-1.5" />}
            Run Reminder Scan
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={handleBulkSend}
            disabled={selectedIds.length === 0 || bulkSend.isLoading}
            className="h-8 text-xs"
          >
            {bulkSend.isLoading ? <Loader className="h-3.5 w-3.5 animate-spin mr-1.5" /> : <Send className="h-3.5 w-3.5 mr-1.5" />}
            Send Selected ({selectedIds.length})
          </Button>
        </div>
        <div className="flex items-center gap-2 flex-1">
          <Input
            placeholder="Search by member name or loan ID..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="h-8 text-xs max-w-xs"
          />
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="h-8 w-36 text-xs">
              <SelectValue placeholder="All statuses" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="sent">Sent</SelectItem>
              <SelectItem value="failed">Failed</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Overdue Table */}
      <Card className="mb-6">
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 text-red-500" />
            Overdue & Upcoming Installments
          </CardTitle>
          <CardDescription>Members with overdue or upcoming payments that need reminders</CardDescription>
        </CardHeader>
        <CardContent>
          {overdueLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader className="h-6 w-6 animate-spin text-green-600" />
            </div>
          ) : filteredOverdue.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <CheckCircle className="h-10 w-10 mx-auto mb-2 text-green-400" />
              <p>No overdue installments found</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-10">
                      <input
                        type="checkbox"
                        checked={selectedIds.length === filteredOverdue.length && filteredOverdue.length > 0}
                        onChange={toggleSelectAll}
                        className="rounded"
                      />
                    </TableHead>
                    <TableHead>Member</TableHead>
                    <TableHead>Loan ID</TableHead>
                    <TableHead>Due Date</TableHead>
                    <TableHead className="text-right">Amount Due</TableHead>
                    <TableHead>Days Overdue</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Last Reminder</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredOverdue.map((item: any) => (
                    <TableRow key={`${item.loanId}-${item.scheduleId}`}>
                      <TableCell>
                        <input
                          type="checkbox"
                          checked={selectedIds.includes(item.scheduleId)}
                          onChange={() => toggleSelect(item.scheduleId)}
                          className="rounded"
                        />
                      </TableCell>
                      <TableCell>
                        <div className="font-medium text-sm">{item.memberName}</div>
                      </TableCell>
                      <TableCell>
                        <span className="text-xs font-mono text-gray-600">{item.loanCode}</span>
                      </TableCell>
                      <TableCell>
                        <span className="text-xs">{item.dueDate ? format(new Date(item.dueDate), "MMM dd, yyyy") : "—"}</span>
                      </TableCell>
                      <TableCell className="text-right">
                        <span className="text-xs font-medium">{formatCurrency(item.amountDue)}</span>
                      </TableCell>
                      <TableCell>
                        {item.daysOverdue > 0 ? (
                          <Badge className="bg-red-100 text-red-700 text-xs">{item.daysOverdue}d overdue</Badge>
                        ) : (
                          <Badge className="bg-amber-100 text-amber-700 text-xs">Due soon</Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        <Badge className={cn("text-xs", PAYMENT_STATUS_COLORS[item.paymentStatus] || "bg-gray-100")}>
                          {item.paymentStatus}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {item.lastReminderType ? (
                          <div className="text-xs">
                            <div>{REMINDER_TYPE_LABELS[item.lastReminderType] || item.lastReminderType}</div>
                            {item.lastReminderAt && (
                              <span className="text-gray-400">{format(new Date(item.lastReminderAt), "MMM dd")}</span>
                            )}
                          </div>
                        ) : (
                          <span className="text-xs text-gray-400">None</span>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => navigate(`/loans/${item.loanCode}`)}
                            className="h-7 px-2 text-xs"
                          >
                            <Eye className="h-3.5 w-3.5" />
                          </Button>
                          {item.memberId && (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => navigate(`/members/${item.memberId}`)}
                              className="h-7 px-2 text-xs"
                            >
                              <Phone className="h-3.5 w-3.5" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Reminder History */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Clock className="h-4 w-4 text-blue-500" />
            Reminder History
          </CardTitle>
          <CardDescription>Log of all reminders sent to members</CardDescription>
        </CardHeader>
        <CardContent>
          {historyLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader className="h-6 w-6 animate-spin text-green-600" />
            </div>
          ) : historyItems.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <Bell className="h-10 w-10 mx-auto mb-2 text-gray-300" />
              <p>No reminders sent yet</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Reminder Code</TableHead>
                    <TableHead>Member</TableHead>
                    <TableHead>Loan ID</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Due Date</TableHead>
                    <TableHead className="text-right">Amount</TableHead>
                    <TableHead>Channel</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Sent At</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {historyItems.map((item: any) => (
                    <TableRow key={item.id}>
                      <TableCell>
                        <span className="text-xs font-mono">{item.reminderCode}</span>
                      </TableCell>
                      <TableCell>
                        <span className="text-sm">{item.memberName}</span>
                      </TableCell>
                      <TableCell>
                        <span className="text-xs font-mono text-gray-600">{item.loanCode}</span>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className="text-xs">
                          {REMINDER_TYPE_LABELS[item.reminderType] || item.reminderType}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <span className="text-xs">{item.dueDate ? format(new Date(item.dueDate), "MMM dd, yyyy") : "—"}</span>
                      </TableCell>
                      <TableCell className="text-right">
                        <span className="text-xs font-medium">{formatCurrency(item.amountDue)}</span>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className="text-xs capitalize">{item.channel}</Badge>
                      </TableCell>
                      <TableCell>
                        <Badge className={cn("text-xs", REMINDER_STATUS_COLORS[item.status] || "bg-gray-100")}>
                          {item.status}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <span className="text-xs text-gray-500">
                          {item.sentAt ? format(new Date(item.sentAt), "MMM dd, HH:mm") : "—"}
                        </span>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}

          {/* Pagination */}
          {historyTotal > 20 && (
            <div className="flex items-center justify-between mt-4">
              <span className="text-xs text-gray-500">
                Showing {(historyPage - 1) * 20 + 1}-{Math.min(historyPage * 20, historyTotal)} of {historyTotal}
              </span>
              <div className="flex items-center gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setHistoryPage((p) => Math.max(1, p - 1))}
                  disabled={historyPage === 1}
                  className="h-7 text-xs"
                >
                  Previous
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setHistoryPage((p) => p + 1)}
                  disabled={historyPage * 20 >= historyTotal}
                  className="h-7 text-xs"
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
